import { type Dispatch, type SetStateAction, useMemo } from "react";
import { ArrowUpToLine, X } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { philosopher } from "@/lib/fonts";
import {
  createWithdrawalRequestInputSchema,
  priceFormat,
} from "muminsmart-utils";
import { headerBg3Style } from "@/assets";
import { toast } from "sonner";

interface WithdrawDrawerProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  contextLabel: string;
  availableBalance: number;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (...args: [number]) => Promise<void>;
}

function WithdrawDrawer({
  open,
  onOpenChange,
  contextLabel,
  availableBalance,
  onConfirm,
}: Readonly<WithdrawDrawerProps>) {
  const withdrawalFormSchema = useMemo(
    () =>
      createWithdrawalRequestInputSchema.extend({
        amount: createWithdrawalRequestInputSchema.shape.amount
          .positive("Please enter a valid amount")
          .max(
            availableBalance,
            "Amount cannot be greater than available balance",
          ),
      }),
    [availableBalance],
  );

  const form = useForm({
    resolver: zodResolver(withdrawalFormSchema),
    defaultValues: {
      amount: 0,
      description: "",
      referenceId: "",
      metadata: {},
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const handleWithdraw = form.handleSubmit(async (values) => {
    try {
      await onConfirm(values.amount);
      form.reset({
        amount: 0,
        description: "",
        referenceId: "",
        metadata: {},
      });
      onOpenChange(false);
    } catch {
      toast.error("Failed to submit withdrawal request");
    }
  });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="overflow-hidden rounded-t-4xl border-none bg-white p-0">
        <Form {...form}>
          <form
            className="relative bg-repeat-x px-7 py-8 pt-3"
            style={headerBg3Style}
            onSubmit={handleWithdraw}
          >
            <DrawerClose
              aria-label="Close withdraw drawer"
              className="absolute right-5 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#4f4f4f] shadow-sm"
            >
              <X className="h-5 w-5" />
            </DrawerClose>

            <h1
              className={`mt-15 text-3xl font-bold text-[#2F2F2F] ${philosopher.className}`}
            >
              Withdraw From {contextLabel}
            </h1>

            <p
              className={`mt-3 text-base font-medium text-[#5b5148] ${philosopher.className}`}
            >
              Available Balance: {priceFormat(availableBalance)}
            </p>

            <div className="mt-6">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <label className="sr-only" htmlFor={field.name}>
                      Enter withdrawal amount
                    </label>
                    <FormControl>
                      <Input
                        id={field.name}
                        type="number"
                        min={1}
                        inputMode="decimal"
                        autoFocus
                        value={field.value || ""}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          field.onChange(
                            nextValue === "" ? 0 : Number(nextValue),
                          );
                        }}
                        onBlur={field.onBlur}
                        placeholder="ENTER WITHDRAW AMOUNT"
                        className="h-14 rounded-full border-[#d8d2cb] bg-[#f2efed] px-6 text-base text-[#5a544f] placeholder:text-sm placeholder:font-semibold placeholder:tracking-[0.08em] placeholder:text-[#77706b]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-10 space-y-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-14 w-full rounded-full bg-[#104d5a] text-lg font-medium text-white hover:bg-[#0c404b] disabled:opacity-60"
              >
                <ArrowUpToLine className="h-5 w-5" />
                {isSubmitting ? "Processing..." : "Withdraw Now"}
              </Button>
            </div>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
}

export default WithdrawDrawer;
